import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Select, Spin } from "antd";
import axios from "axios";
import { useDebounceCallback } from "usehooks-ts";
import { axiosErrorHandler, isNullOrBlank } from '../../../swzutils';
const LazyLoadSelect = ({ url, tenantId, resultLimit = 50, placeholder = 'Select', searchKey, queryParams, value, onChange, onSelected, style, notFoundMessage = 'No results found', optionFilterProp = 'filter', // optionsRenderer method must have a "filter" property on each option otherwise antd will not filter properly
optionsRenderer, allowClear, disabled, onDataLoaded }, exportedRef) => {
    const [fetching, setFetching] = useState(false);
    const [results, setResults] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [initialFetchComplete, setInitialFetchComplete] = useState(false);
    const [fetchedSearchText, setFetchedSearchText] = useState(undefined);
    const debounceFetch = useDebounceCallback(searchTest => fetchResults(searchTest, false), 800);
    const latestRequestId = useRef(0);
    useImperativeHandle(exportedRef, () => ({
        update,
        reset: () => {
            setResults([]);
            setHasMore(true);
            value && handleChange(null); // Clear the selection
            fetchResults(fetchedSearchText, false);
        }
    }));
    useEffect(() => {
        if (initialFetchComplete) {
            fetchSelectionIfRequired();
            value && handleChange(value);
            onDataLoaded && onDataLoaded(results);
        }
    }, [results, value, initialFetchComplete]);
    useEffect(() => {
        // clear selection on tenant change and the results are tenant specific (i.e. tenantId is defined).
        if (initialFetchComplete && tenantId) {
            handleChange(null);
        }
    }, [tenantId]);
    useEffect(() => {
        fetchResults(undefined, true);
    }, [url, tenantId, searchKey]);
    const fetchResults = (searchText, initialFetch) => {
        const requestId = ++latestRequestId.current;
        setFetching(true);
        axios({
            url,
            method: "get",
            headers: {
                'FilterTenantId': tenantId
            },
            params: {
                ...queryParams,
                [searchKey]: searchText,
                limit: resultLimit
            }
        }).then(axiosResponse => {
            if (initialFetch && !initialFetchComplete) {
                // mark the initial fetch as complete even if this response has since
                // been superseded by a newer request, otherwise a race on mount (e.g. a
                // consumer effect calling reset() on the same render pass) can leave
                // initialFetchComplete permanently false
                setInitialFetchComplete(true);
            }
            if (requestId !== latestRequestId.current) {
                // a newer search has since been issued; ignore this now-stale response
                return;
            }
            const responseData = axiosResponse.data;
            setFetchedSearchText(searchText);
            if (responseData.maxResults) {
                setHasMore(responseData.maxResults > responseData.results.length);
            }
            else {
                setHasMore(responseData.hasMore ?? true);
            }
            setResults(responseData.results);
        })
            .catch(axiosErrorHandler)
            .finally(() => {
            if (requestId === latestRequestId.current) {
                setFetching(false);
            }
        });
    };
    const fetchSelectionIfRequired = () => {
        if (value && results.length) {
            // this method makes sure the selected id is in the initial search results
            const selected = results.find(result => result.id == value);
            if (!selected) {
                fetchSelectedResult();
            }
        }
    };
    const update = (result) => {
        const foundIndex = results.findIndex(r => r.id === result.id);
        if (foundIndex >= 0) {
            const resultsCopy = [...results];
            resultsCopy[foundIndex] = result;
            setResults(resultsCopy);
        }
    };
    const fetchSelectedResult = () => {
        if (!value) {
            return;
        }
        setFetching(true);
        axios.get(`${url}/${value}`)
            .then((response) => setResults([...results, response.data]))
            .catch(axiosErrorHandler)
            .finally(() => setFetching(false));
    };
    const handleChange = (value) => {
        onChange && onChange(value);
        if (onSelected) {
            if (value) {
                const selected = results.find(result => result.id == value);
                if (selected) {
                    onSelected(selected);
                }
            }
            else {
                onSelected(null);
            }
        }
    };
    const handleSearch = (searchText) => {
        if (initialFetchComplete && !hasMore && isNullOrBlank(fetchedSearchText)) {
            // All possible results were returned by the initial fetch
            return;
        }
        if ((!hasMore && !isNullOrBlank(searchText) && !isNullOrBlank(fetchedSearchText) && searchText.startsWith(fetchedSearchText))) {
            // Suppress unnecessary network requests that narrow down an existing search.
            // antd's inbuilt string based filter will handle it
            return;
        }
        debounceFetch(searchText);
    };
    return (_jsx(Select, { value: value, placeholder: placeholder, disabled: disabled, allowClear: allowClear, style: style, showSearch: {
            optionFilterProp: optionFilterProp ? [optionFilterProp] : undefined,
            onSearch: handleSearch
        }, onChange: handleChange, popupRender: menu => (_jsx(Spin, { spinning: fetching, children: menu })), notFoundContent: fetching ? _jsx("span", { children: "\u00A0" }) : notFoundMessage, children: optionsRenderer(results) }));
};
export default React.forwardRef(LazyLoadSelect);
